import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { ArrowUpRight, Check, X } from "@phosphor-icons/react";
import { ClipPhoto, EASE_OUT, MaskWords, PageFrame, Seo, useMediaQuery } from "../../kit.jsx";
import { ArrowLink, Btn, Kicker, TEAM_SIZE, clamp01 } from "./shared.jsx";
import "./about.css";

const MODEL = [
  {
    word: "Think",
    text: "Find a real problem and the people who live with it. Ask questions before building anything.",
  },
  {
    word: "Build",
    text: "Turn the idea into something you can test. That can be software, hardware, research or community action.",
  },
  {
    word: "Launch",
    text: "Show the work to mentors and judges. The strongest ideas get feedback, awards and incubation support.",
  },
];

// Where each step's dot sits along the line (0 to 1).
const DESKTOP_STOPS = [0.012, 0.345, 0.678];
const NARROW_STOPS = [0.02, 0.36, 0.7];

function ModelStep({ step, index, draw, stop, reduceMotion }) {
  const wordOpacity = useTransform(draw, (d) => 0.16 + 0.84 * clamp01((d - stop + 0.02) / 0.08));
  const dotScale = useTransform(draw, (d) => clamp01((d - stop) / 0.03));
  return (
    <li className="ab-model__step">
      <span className="ab-model__dot" aria-hidden="true">
        <motion.span style={reduceMotion ? undefined : { scale: dotScale }} />
      </span>
      <span className="ab-model__num">{String(index + 1).padStart(2, "0")}</span>
      <motion.h3 className="ab-model__word" style={reduceMotion ? undefined : { opacity: wordOpacity }}>
        {step.word}
      </motion.h3>
      <p>{step.text}</p>
    </li>
  );
}

function ModelDiagram() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const narrow = useMediaQuery("(max-width: 767px)");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.4"] });
  const draw = useTransform(scrollYProgress, (value) => clamp01(value / 0.9));
  const headOpacity = useTransform(draw, (d) => clamp01((d - 0.94) / 0.06));
  const stops = narrow ? NARROW_STOPS : DESKTOP_STOPS;

  return (
    <section className="ab-model" id="model" aria-labelledby="ab-model-title">
      <header className="ab-model__head">
        <MaskWords id="ab-model-title" className="ab-h2" text="Think, build, launch." />
        <p className="ab-body">
          The same three steps run through everything we organise. AAROH was built around them, and our project guides follow the same order.
        </p>
      </header>
      <div className={`ab-model__stage ${narrow ? "is-narrow" : ""}`} ref={ref}>
        {!narrow && (
          <svg className="ab-model__line" viewBox="0 0 1200 24" preserveAspectRatio="none" aria-hidden="true">
            <path className="ab-model__base" d="M 0 12 H 1186" vectorEffect="non-scaling-stroke" />
            <motion.path
              d="M 0 12 H 1186"
              style={reduceMotion ? undefined : { pathLength: draw }}
              vectorEffect="non-scaling-stroke"
            />
            <motion.path
              className="ab-model__head-mark"
              d="M 1172 2 L 1188 12 L 1172 22"
              style={reduceMotion ? undefined : { opacity: headOpacity }}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        )}
        {narrow && (
          <span className="ab-model__rail" aria-hidden="true">
            <motion.span style={reduceMotion ? undefined : { scaleY: draw }} />
          </span>
        )}
        <ol className="ab-model__steps">
          {MODEL.map((step, index) => (
            <ModelStep
              key={step.word}
              step={step}
              index={index}
              draw={draw}
              stop={stops[index]}
              reduceMotion={reduceMotion}
            />
          ))}
        </ol>
      </div>
      <p className="ab-model__loop">
        Then back to the start. What a team learns at launch becomes the next problem worth thinking about.
      </p>
    </section>
  );
}

function VisionMission() {
  const reduceMotion = useReducedMotion();
  return (
    <section className="ab-vm" aria-labelledby="ab-vm-title">
      <h2 id="ab-vm-title" className="sr-only">Vision and mission</h2>
      <figure className="ab-vm__vision">
        <Kicker>Our vision</Kicker>
        <MaskWords as="p" className="ab-vm__big" text="A launchpad and global resource hub for youth-led innovation." />
        <blockquote className="ab-vm__full">
          <p>
            "To serve as a launchpad and global resource hub for youth-led innovation, transforming how young people interact with technology to solve real-world problems."
          </p>
        </blockquote>
      </figure>
      <motion.figure
        className="ab-vm__mission"
        data-nav-tone="dark"
        initial={reduceMotion ? false : { clipPath: "inset(8% 4% 0% 4% round 28px)" }}
        whileInView={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 1.1, ease: EASE_OUT }}
      >
        <Kicker>Our mission</Kicker>
        <MaskWords
          as="p"
          className="ab-vm__big"
          text="To incubate, mentor and scale changemaking ideas presented by young minds."
          accent="scale"
        />
        <blockquote className="ab-vm__full">
          <p>
            "By providing the resources, network and platform to move forward, Project GRID helps bridge the gap between a student's initial idea and a fully realised, impactful initiative."
          </p>
        </blockquote>
      </motion.figure>
    </section>
  );
}

const WE_ARE = [
  ["A student-run team.", `${TEAM_SIZE} school students run programmes, technology, partnerships and community.`],
  ["The organisers of AAROH and Aurora.", "AAROH was built with Tachyon India and CiPD, IIIT Delhi. Aurora is our own programme."],
  ["A source of free project guides.", "Step-by-step help for students going from a problem to a pitch."],
];

const WE_ARE_NOT = [
  ["The organisers of Metamorphosis.", "Tale of Humankind Foundation and IIT Delhi's outreach office led it. GRID was the outreach partner."],
  ["A registered non-profit yet.", "Our registration as a Section 8 non-profit is in progress and not complete."],
  ["Endorsed by the people we meet.", "A meeting is recorded as a meeting. It is not a partnership."],
];

function Honesty() {
  const reduceMotion = useReducedMotion();
  const row = (index) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.5 },
    transition: { duration: 0.7, delay: index * 0.06, ease: EASE_OUT },
  });
  return (
    <section className="ab-honest" aria-labelledby="ab-honest-title">
      <header className="ab-honest__head">
        <MaskWords id="ab-honest-title" className="ab-h2" text="What we are, and what we are not." />
        <p className="ab-body">The short version.</p>
      </header>
      <div className="ab-honest__cols">
        <div>
          <p className="ab-honest__label"><span className="ab-honest__icon is-yes" aria-hidden="true"><Check weight="bold" /></span>We are</p>
          <ul>
            {WE_ARE.map(([title, text], index) => (
              <motion.li key={title} {...row(index)}>
                <strong>{title}</strong>
                <span>{text}</span>
              </motion.li>
            ))}
          </ul>
        </div>
        <div>
          <p className="ab-honest__label"><span className="ab-honest__icon is-no" aria-hidden="true"><X weight="bold" /></span>We are not</p>
          <ul>
            {WE_ARE_NOT.map(([title, text], index) => (
              <motion.li key={title} {...row(index)}>
                <strong>{title}</strong>
                <span>{text}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
      <div className="ab-legal">
        <span className="ab-kicker">Legal status</span>
        <p>Project GRID is in registration as a Section 8 non-profit. Until that is complete, we are a student-led initiative, not a registered company or charity.</p>
        <ArrowLink to="/governance">How we're run</ArrowLink>
      </div>
    </section>
  );
}

const NEXT = [
  ["Read a programme recap", "See who led each programme and what happened.", "/programmes"],
  ["Find an opportunity", "Programmes and openings you can join.", "/student-opportunities"],
  ["Start a project guide", "Begin with a real-world problem.", "/guides"],
  ["Meet the people responsible", "Every role on the team has a name.", "/team"],
];

function Next() {
  return (
    <section className="ab-next" aria-labelledby="ab-next-title">
      <MaskWords id="ab-next-title" className="ab-h2" text="Where to go from here." />
      <ul>
        {NEXT.map(([title, text, to]) => (
          <li key={to}>
            <Link to={to}>
              <strong>{title}</strong>
              <span>{text}</span>
              <ArrowUpRight weight="bold" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function AboutPage() {
  const reduceMotion = useReducedMotion();
  const appear = (delay) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: EASE_OUT },
  });
  return (
    <PageFrame className="rd-page ab-page ab-page--about">
      <Seo
        title="About Project GRID"
        path="/about"
        description="Learn why Project GRID exists and how its youth-led innovation model works."
      />
      <main>
        <section className="ab-ahero" aria-labelledby="ab-ahero-title">
          <motion.div {...appear(0)}>
            <Kicker>About GRID · What we do</Kicker>
          </motion.div>
          <MaskWords as="h1" id="ab-ahero-title" className="ab-display" text="Age is not a barrier to innovation." onMount delay={0.08} />
          <div className="ab-ahero__row">
            <motion.dl className="ab-ahero__facts" {...appear(0.4)}>
              <div><dt>Founded</dt><dd>December 2025, Delhi</dd></div>
              <div><dt>Founders</dt><dd>Moksh Sindhwani and Tanishq Aggarwal, both 16 at the time</dd></div>
              <div><dt>Run by</dt><dd>{TEAM_SIZE} school students</dd></div>
            </motion.dl>
            <motion.div className="ab-ahero__copy" {...appear(0.5)}>
              <p className="ab-lede">
                Project GRID gives school students a place to work on real ideas. We organise programmes, help with selected collaborations and publish free guides for students starting a project.
              </p>
              <div className="gh-actions">
                <Btn to="/programmes">Our programmes</Btn>
                <Btn href="#model" variant="ghost">How it works</Btn>
              </div>
            </motion.div>
            <ClipPhoto
              className="ab-ahero__photo"
              src="/images/grid-photo-06-p7.jpg"
              alt="Co-founders Moksh Sindhwani and Tanishq Aggarwal at Metamorphosis, IIT Delhi"
              caption="Moksh and Tanishq at Metamorphosis, IIT Delhi."
              sizes="(max-width: 900px) 100vw, 26vw"
              priority
            />
          </div>
        </section>
        <ModelDiagram />
        <VisionMission />
        <Honesty />
        <Next />
      </main>
    </PageFrame>
  );
}
